import { useEffect, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { ExpandMore } from '@mui/icons-material';
import {
  Typography,
  Box,
  Divider,
  Container,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { EventHero, Image } from '@scorenco/components';
import { Types, useEventLive, useTranslation, Locale } from '@scorenco/core';
import { format, startOfToday, subDays } from 'date-fns';
import { fr, de } from 'date-fns/locale';
import { useRouter } from 'next/router';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import { T_KEYS } from '@/translations';

import { LiveWidgetTitle } from './_internal/LiveWidgetTitle';
import {
  WidgetLiveGetTeamsSponsorsDocument,
  WidgetGetEventDataDocument,
  WidgetGetEventIdWithDateFilterDocument,
} from './_internal/Widget.query.generated';
import { WidgetLivePosts } from './_internal/WidgetLivePosts';

const backgroundImagePath = (event: Types.Competitions_Event_Detail) => {
  if (!event) {
    return undefined;
  }

  const category = ['SENIOR', 'U2', 'LOISIR', 'VETERAN'].find((category) =>
    category.startsWith(event.age_category)
  )
    ? 'seniors'
    : 'juniors';
  const gender = event.gender === 'F' ? 'women' : 'men';

  return `/images/event-hero/${event?.sport?.slug}-${gender}-${category}.webp`;
};

export const LiveWidget = ({ widgetTeamId, liveSettings }) => {
  const { t, locale } = useTranslation();
  const [eventId, setEventId] = useState(undefined);
  const [, setTeamId] = useState(undefined);
  const theme = useTheme();
  const router = useRouter();

  const [lastEvent, lastEventData] = useLazyQuery(
    WidgetGetEventIdWithDateFilterDocument,
    {
      variables: {
        dateFilter: -1,
        teamId: widgetTeamId,
      },
      // skip: !teamId,
      context: {
        role: 'anonymous',
      },
    }
  );

  const [nextEvent, nextEventData] = useLazyQuery(
    WidgetGetEventIdWithDateFilterDocument,
    {
      variables: {
        dateFilter: 1,
        teamId: widgetTeamId,
      },
      // skip: !teamId,
      context: {
        role: 'anonymous',
      },
    }
  );

  useEffect(() => {
    if (lastEventData.data?.competitions_event_detail_by_team_id?.[0]) {
      const _event = lastEventData.data.competitions_event_detail_by_team_id[0];
      if (
        _event &&
        new Date(_event.date).getTime() >
          new Date(
            subDays(startOfToday(), liveSettings?.nb_days_update)
          ).getTime()
      ) {
        setEventId(_event.id);
      } else {
        nextEvent();
      }
    }
  }, [lastEventData.data?.competitions_event_detail_by_team_id]);

  useEffect(() => {
    if (nextEventData.data?.competitions_event_detail_by_team_id?.[0]?.id) {
      setEventId(nextEventData.data.competitions_event_detail_by_team_id[0].id);
    } else {
      setEventId(
        lastEventData.data?.competitions_event_detail_by_team_id?.[0]?.id ||
          null
      );
    }
  }, [nextEventData.data?.competitions_event_detail_by_team_id]);

  useEffect(() => {
    if (liveSettings) {
      if (liveSettings?.live_id && !liveSettings?.nb_days_update) {
        setEventId(liveSettings?.live_id);
      } else if (liveSettings?.nb_days_update && !liveSettings?.live_id) {
        setTeamId(widgetTeamId);

        lastEvent();
      } else {
        setEventId(null);
      }
    } else {
      setEventId(undefined);
    }
  }, [liveSettings]);

  const loadEventData = useQuery(WidgetGetEventDataDocument, {
    variables: {
      eventId: Number(eventId),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !eventId,
    context: {
      role: 'anonymous',
    },
  });

  const liveQuery = useEventLive({
    eventId: Number(eventId),
    generateLive: false,
  });

  const live = liveQuery.data?.lives_live?.[0];
  const event = loadEventData.data?.competitions_event_detail_by_id?.[0];

  const loadTeamsSponsorsData = useQuery(WidgetLiveGetTeamsSponsorsDocument, {
    variables: {
      team_id_1: event?.teams?.[0]?.team_id,
      team_id_2: event?.teams?.[1]?.team_id,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !event?.teams?.[0]?.team_id && !event?.teams?.[1]?.team_id,
    context: {
      role: 'anonymous',
    },
  });

  const sponsors = loadTeamsSponsorsData.data?.sponsors_team_sponsor;

  if (eventId === null || eventId === undefined) {
    return (
      <>
        <LiveWidgetTitle />
        <Stack
          spacing={4}
          sx={{
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Container>
            <Typography
              variant="body2"
              component="p"
              sx={{ mt: '6.5rem', mb: '5rem', textAlign: 'center' }}
            >
              {t(T_KEYS.WIDGET_ERROR)}
            </Typography>
          </Container>
        </Stack>
      </>
    );
  }

  return (
    <>
      {event ? (
        <>
          <LiveWidgetTitle status={event.status} />
          <Stack
            spacing={4}
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.vars.palette.primary.dark,
                width: '100%',
                position: 'relative',
              }}
            >
              <img
                css={css`
                  position: absolute;
                  object-fit: cover;
                  opacity: 0.15;
                  width: 100%;
                  height: 100%;
                `}
                src={backgroundImagePath(event)}
                alt=""
              />
              <Container>
                <EventHero
                  css={css`
                    width: 100%;
                    padding-top: 2rem;
                    padding-bottom: 2rem;
                  `}
                  event={event}
                  live={live}
                />
              </Container>
            </Box>
            {!!sponsors?.length && (
              <Container>
                <Stack spacing="1" sx={{ maxWidth: '40rem', m: '0 auto' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      mb: 1,
                    }}
                  >
                    {t(T_KEYS.SPONSORS_LIVE_OFFERED_BY)}
                  </Typography>
                  <Box>
                    <Swiper
                      className="mySwiper"
                      loop={sponsors.length > 1 ? true : false}
                      autoplay={
                        sponsors.length > 1
                          ? {
                              delay: 3000,
                              disableOnInteraction: false,
                            }
                          : false
                      }
                      modules={[Autoplay]}
                    >
                      {sponsors.map((sponsor, i) => (
                        <SwiperSlide
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Image
                            src={sponsor.sponsor?.logo}
                            css={() => css`
                              height: 7rem;

                              width: auto;
                            `}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </Box>
                </Stack>
              </Container>
            )}

            <Container>
              <Accordion
                square
                disableGutters
                elevation={0}
                sx={{ maxWidth: '40rem', m: '0 auto' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    borderBottom: '1px solid',
                    borderBottomColor: theme.vars.palette.divider,
                  }}
                >
                  <Typography>{t(T_KEYS.EVENT_INFORMATIONS)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1} textAlign="center">
                    <Box>
                      {event?.round && (
                        <Typography
                          variant="body1"
                          component="p"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {[
                            event.round.competition_detail?.[0]?.name,
                            event.round.name,
                          ]
                            .filter(Boolean)
                            .join(' - ')}
                        </Typography>
                      )}
                      <Typography variant="body1" component="p">
                        {event.age_category} {event.gender}
                      </Typography>
                    </Box>
                    {event.date && (
                      <Typography variant="body1" component="p">
                        {format(
                          new Date(event.date),
                          t(T_KEYS.EVENT_INFO_SLIDE_DATE_FORMAT),
                          {
                            locale: locale === Locale.de_CH ? de : fr,
                          }
                        )}
                      </Typography>
                    )}
                    {event.place && (
                      <Box>
                        <Typography variant="body1" component="p">
                          {[event.place.address, event.place.postal_code]
                            .filter(Boolean)
                            .join(' - ')}{' '}
                          {event.place.city?.name.toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Container>
            <Container>
              <Divider sx={{ width: '100%' }} />
            </Container>
            <Container>
              <Box
                sx={{
                  maxWidth: `40rem`,
                  m: '0 auto',
                }}
              >
                {live && <WidgetLivePosts live={live} event={event} />}
              </Box>
            </Container>
          </Stack>
        </>
      ) : (
        <>
          <LiveWidgetTitle status={null} />
          <Stack
            spacing={4}
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Skeleton width="100%" height="12rem" variant="rectangular" />
            <Skeleton width="100%" height="15rem" variant="rectangular" />
          </Stack>
        </>
      )}
    </>
  );
};
